import { ReactNode, createContext, useContext, useCallback, useMemo } from "react";

import * as Sentry from "@sentry/react";
import {
    useConnectWallet,
    BaseConnectedWalletType,
    PrivyErrorCode,
    ConnectWalletModalOptions
} from "@privy-io/react-auth";
import { useDispatch } from "react-redux";

import { updateStateWalletAddress, useAuthStorage, useResetAllWalletData } from "../reducers";
import { normalizePrivyWalletClientType } from "./privyHelpers";

// Module-level error handler for Privy connection errors
const handlePrivyConnectionError = (error: PrivyErrorCode) => {
    // eslint-disable-next-line no-console
    console.error("Error connecting wallet", error);

    // Only log unexpected errors to Sentry
    Sentry.captureMessage(`Wallet connection error: ${error}`, {
        level: "error",
        tags: {
            feature: "wallet_connection",
            error_code: error
        }
    });
};

interface PrivyWalletConnectionContextValue {
    connectWallet: (options?: ConnectWalletModalOptions) => void;
}

const PrivyWalletConnectionContext = createContext<PrivyWalletConnectionContextValue | null>(null);

export function PrivyWalletConnectionProvider({ children }: { children: ReactNode }) {
    const { resetCookies, setWalletCookie } = useAuthStorage();
    const dispatch = useDispatch();
    const resetAllWalletData = useResetAllWalletData();

    const onSuccess = useCallback(
        ({ wallet }: { wallet: BaseConnectedWalletType }) => {
            resetCookies();
            const privyWalletAddress = wallet.address;
            const privyWalletClientType = normalizePrivyWalletClientType(wallet.walletClientType);

            dispatch(
                updateStateWalletAddress({ address: privyWalletAddress, walletClientType: privyWalletClientType })
            );
            setWalletCookie(privyWalletAddress, privyWalletClientType);

            resetAllWalletData();
        },
        [resetAllWalletData, setWalletCookie, dispatch, resetCookies]
    );

    // Privy connectWallet hook - only instantiated once here
    const { connectWallet } = useConnectWallet({
        onSuccess,
        onError: handlePrivyConnectionError
    });

    const contextValue = useMemo(
        () => ({
            connectWallet: (options?: ConnectWalletModalOptions) =>
                connectWallet({
                    ...options,
                    walletChainType: "solana-only"
                })
        }),
        [connectWallet]
    );

    return (
        <PrivyWalletConnectionContext.Provider value={contextValue}>{children}</PrivyWalletConnectionContext.Provider>
    );
}

export function usePrivyWalletConnect() {
    const context = useContext(PrivyWalletConnectionContext);
    if (!context) {
        throw new Error("usePrivyWalletConnect must be used within PrivyWalletConnectionProvider");
    }
    return context;
}
